/**
 * The second-display surface.
 *
 * This window is a projector, not a workspace: no library, no editor, no
 * menus. State arrives over Tauri events from the document window so the two
 * cannot drift, and clicks here only ask that window to advance.
 */

import { useEffect, useState } from "react";
import { emit, listen } from "@tauri-apps/api/event";
import { PresenterStage } from "@/components/editor/PresenterStage";
import {
  PRESENTER_CONTROL,
  PRESENTER_EVENT,
  PRESENTER_READY,
  type PresenterControl,
  type PresenterState,
} from "@/lib/presenter";

export default function PresentPage() {
  const [state, setState] = useState<PresenterState | null>(null);

  useEffect(() => {
    document.documentElement.dataset.window = "presenter";
    void emit(PRESENTER_READY);
    let unlistenState: (() => void) | undefined;
    void listen<PresenterState>(PRESENTER_EVENT, (event) => {
      setState(event.payload);
    }).then((fn) => {
      unlistenState = fn;
    });
    return () => {
      unlistenState?.();
    };
  }, []);

  useEffect(() => {
    const send = (control: PresenterControl) => {
      void emit(PRESENTER_CONTROL, control);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const handled = () => {
        event.preventDefault();
        event.stopPropagation();
      };
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
        case "PageDown":
        case " ":
        case "Enter":
        case "n":
        case "N":
          handled();
          send("next");
          break;
        case "ArrowLeft":
        case "ArrowUp":
        case "PageUp":
        case "Backspace":
        case "p":
        case "P":
          handled();
          send("prev");
          break;
        case "Home":
          handled();
          send("home");
          break;
        case "End":
          handled();
          send("end");
          break;
        case "b":
        case "B":
        case ".":
          handled();
          send("blank");
          break;
        case "Escape":
          handled();
          send("close");
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  if (!state) {
    return <div className="h-screen w-screen bg-black" />;
  }

  return (
    <div
      className="relative h-screen w-screen bg-black"
      onClick={() => void emit(PRESENTER_CONTROL, "next")}
    >
      <PresenterStage
        slides={state.slides}
        index={state.index}
        blanked={state.blanked}
        style={state.style}
      />
    </div>
  );
}
